import { LanguageProvider, Trans } from './i18n/LanguageProvider';
import React, { Suspense, lazy } from 'react';
import {BrowserRouter, Route, Routes} from 'react-router-dom';
import './App.css';
import { NotificationProvider } from './contexts/NotificationContext';
import NotificationToast from './components/NotificationToast';

// EAGER LOAD: Only load WishlistGamePage immediately (it's the landing page)
import WishlistGamePage from './WishlistGamePage';
// DataFast automatically tracks pageviews - no PageViewTracker needed

// LAZY LOAD: Everything else loads on-demand
const HomePage = lazy(() => import('./HomePage'));
const HowToPlay = lazy(() => import('./HowToPlay'));
const CarsPage = lazy(() => import('./CarsPage'));
const StreamerPage = lazy(() => import('./StreamerPage'));
const Streamers = lazy(() => import('./Streamers'));
const DriversPage = lazy(() => import('./DriversPage'));
const SeasonPage = lazy(() => import('./SeasonPage'));
const RacesPage = lazy(() => import('./RacesPage'));
const RacePage = lazy(() => import('./RacePage'));
const DriverPage = lazy(() => import('./DriverPage'));
const CarPage = lazy(() => import('./CarPage'));
const HostPageWrapper = lazy(() => import('./HostPage').then(module => ({ default: module.HostPageWrapper })));
const NotFoundPage = lazy(() => import('./NotFoundPage'));
const PlayPage = lazy(() => import('./PlayPage'));
const MainPage = lazy(() => import('./MainPage'));
const TwitchPage = lazy(() => import('./TwitchPage'));
const InventoryPage = lazy(() => import('./InventoryPage'));
const GaragePage = lazy(() => import('./GaragePage'));
const StorePage = lazy(() => import('./StorePage'));
const DealershipPage = lazy(() => import('./DealershipPage'));
const Stats = lazy(() => import('./StatsPage'));
const CodexPage = lazy(() => import('./CodexPage'));
const GooglePage = lazy(() => import('./GooglePage'));
const Auth0Page = lazy(() => import('./Auth0Page'));
const AccountLinkPage = lazy(() => import('./AccountLinkPage'));
const PrivacyPolicy = lazy(() => import('./PrivacyPage'));
const TermsOfService = lazy(() => import('./TermsPage'));
const LogoutPage = lazy(() => import('./LogoutPage'));
const LobbyWrapper = lazy(() => import('./Lobby').then(module => ({ default: module.LobbyWrapper })));
const LeaderboardWrapper = lazy(() => import('./Lobby').then(module => ({ default: module.LeaderboardWrapper })));
const StreamLobbyWrapper = lazy(() => import('./Lobby').then(module => ({ default: module.StreamLobbyWrapper })));
const HDLobbyWrapper = lazy(() => import('./Lobby').then(module => ({ default: module.HDLobbyWrapper })));
const TwitchLobbyWrapper = lazy(() => import('./Lobby').then(module => ({ default: module.TwitchLobbyWrapper })));
const YouTubeLobbyWrapper = lazy(() => import('./Lobby').then(module => ({ default: module.YouTubeLobbyWrapper })));
const DriftPage = lazy(() => import('./DriftPage'));
const AdminPage = lazy(() => import('./AdminPage'));
const ModelPage = lazy(() => import('./ModelPage'));
const BrowserTestPage = lazy(() => import('./BrowserTestPage'));
const Animation1Page = lazy(() => import('./Animation1Page'));
const Animation1Display = lazy(() => import('./Animation1Display'));
const Animation2Page = lazy(() => import('./Animation2Page'));
const Animation2Display = lazy(() => import('./Animation2Display'));
const QuestCompleteDisplay = lazy(() => import('./QuestCompleteDisplay'));
const Animation3Page = lazy(() => import('./Animation3Page'));
const Animation3Display = lazy(() => import('./Animation3Display'));
const WishlistPage = lazy(() => import('./WishlistPage'));
const PlinkoPage = lazy(() => import('./PlinkoPage'));
const PlinkoDisplay = lazy(() => import('./PlinkoDisplay'));
const PlinkoDrop = lazy(() => import('./PlinkoDisplay')); // New route to avoid cache
const PlinkoResultPage = lazy(() => import('./PlinkoResultPage'));
const BingoPage = lazy(() => import('./BingoPage'));
const BingoMockPage = lazy(() => import('./BingoMockPage'));
const BingoWinnerDisplay = lazy(() => import('./BingoWinnerDisplay'));
const RewardPage = lazy(() => import('./RewardPage'));
const RewardPage2 = lazy(() => import('./RewardPage2'));
const SpeedometerResultPage = lazy(() => import('./SpeedometerResultPage'));
const SpeedometerLookupPage = lazy(() => import('./SpeedometerLookupPage'));
const WrappyPage = lazy(() => import('./WrappyPage'));

interface AppState {
  initialized: boolean;
};

class App extends React.Component<{}, AppState> {
  constructor(props = {}) {
    super(props);

    this.state = {
      initialized: true  // No need to wait - lazy loading handles everything!
    }
  }

  onUpdate() {
    this.forceUpdate();
  }
  
  render() {
    return (
      <LanguageProvider><NotificationProvider>
        <BrowserRouter>
          <NotificationToast />
          <Suspense fallback={
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100vh',
              background: 'linear-gradient(135deg, #0f1a2e 0%, #1a2744 50%, #0f1a2e 100%)',
              color: '#ffd166',
              fontSize: '1.5em',
              fontWeight: '700'
            }}>
              <Trans message="Loading..." />
            </div>
          }>
          <Routes>
          <Route path='/wishlist' element={<WishlistGamePage />} />
          <Route path='/wishlist-archived' element={<WishlistPage />} />
          {/* Animation display routes - standalone without main layout for transparent backgrounds */}
          <Route path='/animation1-display' element={<Animation1Display />} />
          <Route path='/animation2-display' element={<Animation2Display />} />
          <Route path='/animation3-display' element={<Animation3Display />} />
          <Route path='/quest-complete' element={<QuestCompleteDisplay />} />
          <Route path='/plinko-display' element={<PlinkoDisplay />} />
          <Route path='/plinko-drop' element={<PlinkoDrop />} />
          <Route path='/plinko/:resultId' element={<PlinkoResultPage />} />
          {/* Bingo routes - standalone pages */}
          <Route path='/bingo-winner' element={<BingoWinnerDisplay />} />
          <Route path='/bingo/mock' element={<BingoMockPage />} />
          <Route path='/bingo/:channel' element={<BingoPage />} />
          {/* Reward/Strength game display - standalone for transparent background */}
          <Route path='/reward' element={<RewardPage />} />
          <Route path='/reward2' element={<RewardPage2 />} />
          <Route path='/speedometer/:resultId' element={<RewardPage2 />} />
          <Route path='/speedometer-lookup' element={<SpeedometerLookupPage />} />
          {/* Nova Wrappy - Spotify Wrapped style year review */}
          <Route path='/wrappy/:username' element={<WrappyPage />} />
          <Route path='/' element={<MainPage />}>
            <Route index element={<HomePage />} />
            <Route path='/how-to-play' element={<HowToPlay />} />
            <Route path='/streamers' element={<Streamers />} />
            <Route path='/codex' element={<CodexPage />} />
            <Route path='/garage' element={<GaragePage />} />
            <Route path='/inventory' element={<InventoryPage />} />
            <Route path='/races' element={<RacesPage />} />
            <Route path='/race/:id' element={<RacePage />} />
            <Route path='/season' element={<SeasonPage />} />
            <Route path='/drivers' element={<DriversPage />} />
            <Route path='/driver/:id' element={<DriverPage />} />
            <Route path='/cars' element={<CarsPage />} />
            <Route path='/car/:id' element={<CarPage />} />
            <Route path='/streamer' element={<StreamerPage />} />
            <Route path='/play' element={<PlayPage />} />
            <Route path='/host/:id' element={<HostPageWrapper />} />
            <Route path='/twitch' element={<TwitchPage onUpdate={()=>this.onUpdate()}/>} />
            <Route path='/stats' element={<Stats/>} />
            <Route path='/drift' element={<DriftPage />} />
            <Route path='/store' element={<StorePage />} />
            <Route path='/dealership' element={<DealershipPage />} />
            <Route path='/admin' element={<AdminPage />} />
            <Route path='/models' element={<ModelPage />} />
            <Route path='/browsertest' element={<BrowserTestPage />} />
            <Route path='/animation1' element={<Animation1Page />} />
            <Route path='/animation2' element={<Animation2Page />} />
            <Route path='/animation3' element={<Animation3Page />} />
            <Route path='/plinko' element={<PlinkoPage />} />
            <Route path="/google" element={<GooglePage onUpdate={()=>this.onUpdate()} />} />
            <Route path="/auth0" element={<Auth0Page onUpdate={()=>this.onUpdate()} />} />
            <Route path="/account-link" element={<AccountLinkPage />} />
            <Route path="/lobby/:lobbyName" element={<LobbyWrapper />} />
            <Route path="/lobby/:lobbyName/stream" element={<StreamLobbyWrapper />} />
            <Route path="/lobby/:lobbyName/stream/hd" element={<HDLobbyWrapper />} />
            <Route path="/lobby/:lobbyName/:league" element={<LobbyWrapper />} />
            <Route path="/lobby/:lobbyName/:league/stream" element={<StreamLobbyWrapper />} />
            <Route path="/lobby/:lobbyName/:league/stream/hd" element={<HDLobbyWrapper />} />
            {/* Twitch embed lobby routes */}
            <Route path="/twitch/lobby/:lobbyName" element={<TwitchLobbyWrapper />} />
            <Route path="/twitch/lobby/:lobbyName/:league" element={<TwitchLobbyWrapper />} />
            {/* YouTube embed lobby routes */}
            <Route path="/youtube/lobby/:lobbyName" element={<YouTubeLobbyWrapper />} />
            <Route path="/youtube/lobby/:lobbyName/:league" element={<YouTubeLobbyWrapper />} />
            <Route path="/leaderboard/:lobbyName" element={<LeaderboardWrapper />} />
            <Route path="/leaderboard/:lobbyName/:league" element={<LeaderboardWrapper />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="*" element={<NotFoundPage />} />
            <Route path="/logout" element={<LogoutPage />} />
          </Route>
          </Routes>
          </Suspense>
        </BrowserRouter>
      </NotificationProvider></LanguageProvider>
    );
  }
}

export default App;